import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmModal from '../UI/ConfirmModal';
import { useLang } from '../../i18n/LanguageContext';
import logo from '../../logo.jpg';
import './Orders.css';

import API_BASE_URL from '../../config';

// Absolute URL so the logo loads inside the print iframe
const LOGO_URL = `${window.location.origin}${logo}`;

const getItems = (order) => {
  const raw = order?.items;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
  return [];
};

const tableDisplay = (order) =>
  order.tableNumber === -1
    ? 'Takeaway'
    : order.tableNumber === 0
      ? 'From Website'
      : `Table ${order.tableNumber}`;

const Orders = ({ showNotification }) => {
  const { t, lang } = useLang();
  // Show the dish's Chinese name when the dashboard is in Chinese (falls back to English)
  const dishName = (item) => (lang === 'zh' && item.nameZh ? item.nameZh : item.name);
  const [activeTab, setActiveTab] = useState('new');
  const [orders, setOrders] = useState({ new: [], preparing: [], ready: [], completed: [], cancelled: [] });
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const previousNewCountRef = useRef(0);
  const esRef = useRef(null);
  const pollingRef = useRef(null);
  const safetyPollRef = useRef(null); // always-on 15s poll — catches orders SSE may miss

  // History filters
  const [historySubTab, setHistorySubTab] = useState('completed');
  const [timeFilter, setTimeFilter] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Confirm modals
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, orderId: null, label: '' });

  // Delete by date range
  const [deleteFrom, setDeleteFrom] = useState('');
  const [deleteTo, setDeleteTo] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeletePanel, setShowDeletePanel] = useState(false);

  // Individual order selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteSelectedConfirm, setDeleteSelectedConfirm] = useState(false);

  // Set estimated ready time for an order
  const [etaModal, setEtaModal] = useState({ open: false, orderId: null });
  const [etaMinutes, setEtaMinutes] = useState(15);

  const playSound = () => {
    const soundEnabled = localStorage.getItem('soundEnabled') !== 'false';
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch {}
  };

  // ── Receipt building (80mm thermal format) ───────────────────────────────
  // Builds ONE receipt copy. showPrices=false produces a price-free kitchen copy.
  // mergedItems / orderNumbers / totals let the same builder render a combined bill.
  const buildReceiptCopy = (order, { title, showPrices }) => {
    const items = order._mergedItems || getItems(order);
    const orderNos = order._orderNumbers
      || [order.orderNumber || order._id?.slice(-6).toUpperCase()];
    const totalAmount = order._combinedTotal != null ? order._combinedTotal : Number(order.totalAmount);
    const salesTax = order._combinedTax != null ? order._combinedTax : Number(order.salesTaxAmount || 0);
    const adminDiscount = order._combinedAdminDiscount != null ? order._combinedAdminDiscount : Number(order.orderDiscountAmount || 0);
    const grandTotal = totalAmount + salesTax - adminDiscount;

    // S.N | Item No. | Item (English / 中文) | Qty | [Price | Total]
    const rows = items.map((item, idx) => `
      <tr>
        <td style="text-align:center">${idx + 1}</td>
        <td style="text-align:center;white-space:nowrap;font-weight:600">${item.dishNumber || '-'}</td>
        <td style="width:100%;max-width:${showPrices ? '140px' : '200px'};word-break:break-word">${item.name}${item.nameZh ? ` / ${item.nameZh}` : ''}${item.variantLabel ? ` <span style="font-size:9px;color:#a05a2c">(${item.variantLabel})</span>` : ''}${item.specialInstructions ? `<br/><span style="font-size:9px;color:#666">📝 ${item.specialInstructions}</span>` : ''}</td>
        <td style="text-align:center">${item.quantity}</td>
        ${showPrices ? `<td style="text-align:right;white-space:nowrap">¥${Number(item.price).toFixed(2)}</td>
        <td style="text-align:right;white-space:nowrap">¥${Number(item.subtotal).toFixed(2)}</td>` : ''}
      </tr>`).join('');

    return `
    <div class="copy">
      <div class="head">
        <img class="logo" src="${LOGO_URL}" alt="" />
        <h1>Dewan-e-Khass</h1>
      </div>
      <p class="center" style="font-size:9px">Authentic Restaurant</p>
      <p class="center copy-title">${title}</p>
      <p class="center" style="font-size:9px;margin-top:2px">${new Date(order.createdAt || Date.now()).toLocaleString()}</p>
      <div class="divider"></div>
      <div class="row"><span>Order #</span><b>${orderNos.join(' + ')}</b></div>
      <div class="row"><span>Type</span><b>${tableDisplay(order)}</b></div>
      ${order.orderNote ? `<div class="note">Note: ${order.orderNote}</div>` : ''}
      <div class="divider"></div>
      <table>
        <thead><tr>
          <th style="text-align:center">S.N</th>
          <th style="text-align:center">#</th>
          <th style="text-align:left">Item</th>
          <th style="text-align:center">Qty</th>
          ${showPrices ? `<th style="text-align:right">Price</th><th style="text-align:right">Total</th>` : ''}
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="divider"></div>
      ${showPrices ? `
        <div class="row"><span>Total</span><span>¥${totalAmount.toFixed(2)}</span></div>
        <div class="row"><span>Sales Tax</span><span>¥${salesTax.toFixed(2)}</span></div>
        <div class="row"><span>Discount</span><span>-¥${adminDiscount.toFixed(2)}</span></div>
        <div class="row total"><span>Grand Total</span><span>¥${grandTotal.toFixed(2)}</span></div>
        <div class="divider"></div>
        <p class="center" style="margin-top:3mm">Thank you for dining with us!</p>
        <p class="center">★ Dewan-e-Khass ★</p>
      ` : `<p class="center" style="margin-top:3mm;font-weight:bold">*** KITCHEN — PREP ORDER ***</p>`}
      <p style="margin-top:8mm"></p>
    </div>`;
  };

  // Send pre-built copy HTML to the printer via a hidden iframe (no popup).
  const printCopies = (copiesHtml) => {
    const html = `<!DOCTYPE html><html><head><title>Receipt</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Courier New',monospace;font-size:11px;width:80mm;margin:0 auto;padding:3mm}
      h1{font-size:15px;font-weight:bold;text-align:center;margin-bottom:2px}
      .head{display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:2px}
      .head .logo{width:34px;height:34px;object-fit:contain}
      .head h1{margin-bottom:0}
      .center{text-align:center}.divider{border-top:1px dashed #000;margin:3mm 0}
      .copy-title{font-size:10px;font-weight:bold;letter-spacing:1px;margin-top:3px}
      .row{display:flex;justify-content:space-between;margin-bottom:2px}
      table{width:100%;border-collapse:collapse;font-size:10px}
      th{border-top:1px solid #000;border-bottom:1px solid #000;padding:3px 5px;font-size:10px}
      td{padding:3px 5px}
      /* Vertical divider lines between every column (keeps the receipt from looking cramped) */
      th,td{border-right:1px solid #000}
      th:last-child,td:last-child{border-right:none}
      .total{font-size:13px;font-weight:bold;border-top:2px solid #000;padding-top:2mm;margin-top:2mm}
      .note{border:1px dashed #999;padding:2mm;font-size:9px;margin:2mm 0}
      .copy{page-break-after:always}
      .copy:last-child{page-break-after:auto}
      @media print{body{width:80mm}@page{size:80mm auto;margin:0}}
    </style></head><body>${copiesHtml}</body></html>`;

    const existing = document.getElementById('receipt-print-frame');
    if (existing) existing.remove();
    const iframe = document.createElement('iframe');
    iframe.id = 'receipt-print-frame';
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 400);
  };

  // Print 3 copies for a single order: Customer + Records (priced) + Kitchen (no price).
  const printReceipt = (order) => {
    const copies =
      buildReceiptCopy(order, { title: 'CUSTOMER COPY', showPrices: true }) +
      buildReceiptCopy(order, { title: 'RECORDS COPY', showPrices: true }) +
      buildReceiptCopy(order, { title: 'KITCHEN COPY', showPrices: false });
    printCopies(copies);
  };

  // Print ONE unified bill merging several orders (Customer + Records copies, priced).
  const printCombinedReceipt = (orderList) => {
    if (!orderList || orderList.length === 0) return;
    const mergedItems = orderList.flatMap(o => getItems(o));
    const combinedTotal = orderList.reduce((s, o) => s + Number(o.totalAmount || 0), 0);
    const combinedTax = orderList.reduce((s, o) => s + Number(o.salesTaxAmount || 0), 0);
    const combinedAdminDiscount = orderList.reduce((s, o) => s + Number(o.orderDiscountAmount || 0), 0);
    const orderNumbers = orderList.map(o => o.orderNumber || o._id?.slice(-6).toUpperCase());
    // Synthesize a virtual order carrying the merged data; reuse the base order for table/status/date.
    const base = orderList[0];
    const virtual = {
      ...base,
      _mergedItems: mergedItems,
      _orderNumbers: orderNumbers,
      _combinedTotal: combinedTotal,
      _combinedTax: combinedTax,
      _combinedAdminDiscount: combinedAdminDiscount,
      orderNote: orderList.map(o => o.orderNote).filter(Boolean).join(' | ') || null,
      couponCode: orderList.map(o => o.couponCode).filter(Boolean).join(', ') || null,
    };
    const copies =
      buildReceiptCopy(virtual, { title: 'COMBINED BILL — CUSTOMER COPY', showPrices: true }) +
      buildReceiptCopy(virtual, { title: 'COMBINED BILL — RECORDS COPY', showPrices: true });
    printCopies(copies);
  };

  // ── Bulk delete by date range ─────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (!deleteFrom || !deleteTo) {
      showNotification?.('Please select both From and To dates', 'error');
      return;
    }
    setDeleting(true);
    try {
      const token = localStorage.getItem('ownerToken');
      const res = await axios.delete(
        `${API_BASE_URL}/orders/bulk-delete?from=${deleteFrom}&to=${deleteTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        showNotification?.(`${res.data.deletedCount} orders deleted successfully`, 'success');
        setDeleteFrom(''); setDeleteTo('');
        setDeleteConfirm(false); setShowDeletePanel(false);
        fetchOrders();
      }
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Delete selected individual orders ────────────────────────────────────
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('ownerToken');
      const res = await axios.delete(`${API_BASE_URL}/orders/delete-selected`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: Array.from(selectedIds) }
      });
      if (res.data.success) {
        showNotification?.(`${res.data.deletedCount} order${res.data.deletedCount !== 1 ? 's' : ''} deleted`, 'success');
        setSelectedIds(new Set());
        setDeleteSelectedConfirm(false);
        fetchOrders();
      }
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectOrder = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (orderList) => {
    if (selectedIds.size === orderList.length && orderList.every(o => selectedIds.has(o._id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orderList.map(o => o._id)));
    }
  };

  const categoriseOrders = (allOrders) => ({
    new: allOrders.filter(o => o.status === 'New' || o.status === 'Pending'),
    preparing: allOrders.filter(o => o.status === 'Preparing'),
    ready: allOrders.filter(o => o.status === 'Ready'),
    completed: allOrders.filter(o => o.status === 'Completed'),
    cancelled: allOrders.filter(o => o.status === 'Cancelled'),
  });

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      const res = await axios.get(`${API_BASE_URL}/orders/all-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const categorised = categoriseOrders(res.data.data);
        setOrders(categorised);

        if (categorised.new.length > previousNewCountRef.current && previousNewCountRef.current > 0) {
          setNewOrderAlert(true);
          playSound();
          showNotification?.('New order received!', 'success');
          setTimeout(() => setNewOrderAlert(false), 4000);
        }
        previousNewCountRef.current = categorised.new.length;
        setLoading(false);
      }
    } catch (err) {
      console.error('Fetch orders error:', err);
      showNotification?.('Failed to load orders.', 'error');
    }
  }, [showNotification]);

  // SSE connection with polling fallback
  useEffect(() => {
    const token = localStorage.getItem('ownerToken');
    if (!token) return;

    fetchOrders();

    // Safety poll — runs every 5s regardless of SSE state.
    // Guarantees no order waits more than 5s even if SSE is delayed or missed.
    safetyPollRef.current = setInterval(fetchOrders, 5000);

    const connectSSE = () => {
      if (esRef.current) { esRef.current.close(); }

      const es = new EventSource(`${API_BASE_URL}/sse/orders?token=${encodeURIComponent(token)}`);
      esRef.current = es;

      es.addEventListener('connected', () => {
        setSseConnected(true);
        // Stop the fast fallback poll — safety poll continues in background
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      });

      es.addEventListener('new_order', () => { fetchOrders(); });
      es.addEventListener('order_updated', () => { fetchOrders(); });

      es.onerror = () => {
        setSseConnected(false);
        es.close();
        // Fast fallback poll while SSE is down
        if (!pollingRef.current) {
          const interval = parseInt(localStorage.getItem('refreshInterval') || '5000');
          pollingRef.current = setInterval(fetchOrders, interval);
        }
        // Retry SSE faster — 3s instead of 10s
        setTimeout(connectSSE, 3000);
      };
    };

    connectSSE();

    return () => {
      esRef.current?.close();
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (safetyPollRef.current) clearInterval(safetyPollRef.current);
    };
  }, []); // eslint-disable-line

  const updateOrderStatus = async (orderId, newStatus, extra = {}, onSuccess = null) => {
    try {
      const token = localStorage.getItem('ownerToken');
      const res = await axios.put(
        `${API_BASE_URL}/orders/update-status/${orderId}`,
        { status: newStatus, ...extra },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.data.success) {
        // Show refund outcome message if this was a cancellation of a paid order
        const msg = res.data.message || `Order ${newStatus.toLowerCase()} successfully!`;
        const isWarning = msg.toLowerCase().startsWith('order cancelled. warning');
        showNotification?.(msg, isWarning ? 'error' : 'success');
        if (onSuccess) onSuccess(res.data.data);
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to update order status.', 'error');
    }
  };

  // ── Collective bill: resolve selected active orders + print unified receipt ─
  const getSelectedActiveOrders = () => {
    const active = [...orders.new, ...orders.preparing, ...orders.ready];
    return active.filter(o => selectedIds.has(o._id));
  };

  const handlePrintCombined = () => {
    const list = getSelectedActiveOrders();
    if (list.length < 2) {
      showNotification?.('Select at least 2 orders to combine', 'error');
      return;
    }
    printCombinedReceipt(list);
  };

  const handleAcceptOrder = (orderId) => {
    // Find order data for receipt (from new list or selected modal)
    const orderData = orders.new.find(o => o._id === orderId) || selectedOrder;
    updateOrderStatus(orderId, 'Preparing', {}, (updatedOrder) => {
      printReceipt(updatedOrder || orderData);
    });
  };
  const handleMarkReady = (orderId) => updateOrderStatus(orderId, 'Ready');

  const handleCompleteOrder = (orderId) => {
    setConfirmModal({ open: true, action: 'complete', orderId, label: 'Mark this order as completed?' });
  };
  const handleCancelOrder = (orderId) => {
    setConfirmModal({ open: true, action: 'cancel', orderId, label: 'Cancel this order? This cannot be undone.' });
  };

  const handleConfirmAction = () => {
    const { action, orderId } = confirmModal;
    setConfirmModal({ open: false, action: null, orderId: null, label: '' });
    if (action === 'complete') updateOrderStatus(orderId, 'Completed');
    if (action === 'cancel') updateOrderStatus(orderId, 'Cancelled');
  };

  const handleSetETA = async () => {
    const eta = new Date(Date.now() + etaMinutes * 60000);
    await updateOrderStatus(etaModal.orderId, 'Preparing', { estimatedReadyTime: eta.toISOString() });
    setEtaModal({ open: false, orderId: null });
  };

  const filterOrdersByTime = (orderList) => {
    const now = new Date();
    if (timeFilter === 'today') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return orderList.filter(o => new Date(o.createdAt) >= today);
    }
    if (timeFilter === 'week') {
      const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
      return orderList.filter(o => new Date(o.createdAt) >= weekAgo);
    }
    if (timeFilter === 'month') {
      const monthAgo = new Date(now); monthAgo.setMonth(now.getMonth() - 1);
      return orderList.filter(o => new Date(o.createdAt) >= monthAgo);
    }
    if (timeFilter === 'year') {
      const yearAgo = new Date(now); yearAgo.setFullYear(now.getFullYear() - 1);
      return orderList.filter(o => new Date(o.createdAt) >= yearAgo);
    }
    if (timeFilter === 'custom' && customStart && customEnd) {
      const s = new Date(customStart); s.setHours(0, 0, 0, 0);
      const e = new Date(customEnd); e.setHours(23, 59, 59, 999);
      return orderList.filter(o => { const d = new Date(o.createdAt); return d >= s && d <= e; });
    }
    return orderList;
  };

  const searchOrders = (orderList) => {
    if (!searchTerm.trim()) return orderList;
    const t = searchTerm.toLowerCase();
    return orderList.filter(o =>
      (o.orderNumber && o.orderNumber.toLowerCase().includes(t)) ||
      o._id.toLowerCase().includes(t) ||
      o.tableNumber.toString().includes(t)
    );
  };

  const getFilteredCompleted = () => searchOrders(filterOrdersByTime(orders.completed));
  const getFilteredCancelled = () => searchOrders(filterOrdersByTime(orders.cancelled));

  const formatTime = (date) => new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const getTabCount = (tab) => {
    if (tab === 'history') return orders.completed.length + orders.cancelled.length;
    return orders[tab]?.length || 0;
  };

  const renderOrderCard = (order, type) => (
    <motion.div
      key={order._id}
      className={`order-card status-${order.status.toLowerCase()}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ scale: 1.02 }}
      onClick={() => setSelectedOrder(order)}
    >
      <div className="order-card-header">
        <div className="order-id-wrap">
          <input
            type="checkbox"
            className="order-select-check"
            checked={selectedIds.has(order._id)}
            onClick={e => toggleSelectOrder(order._id, e)}
            onChange={() => {}}
            title="Select for combined bill"
          />
          <div className="order-id">#{order.orderNumber || order._id.slice(-6).toUpperCase()}</div>
        </div>
        <div className="order-badges">
          <div className={`status-badge ${order.status.toLowerCase()}`}>{order.status}</div>
        </div>
      </div>

      <div className="order-card-body">
        <div className="order-table">
          <i className={`bi ${order.tableNumber === -1 ? 'bi-bag' : order.tableNumber === 0 ? 'bi-globe' : 'bi-people'}`}></i>
          <span>{tableDisplay(order)}</span>
        </div>

        {order.estimatedReadyTime && (
          <div className="order-eta">
            <i className="bi bi-clock me-1"></i>
            Ready by: {new Date(order.estimatedReadyTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}

        {order.orderNote && (
          <div className="order-note-preview">
            <i className="bi bi-chat-left-text me-1"></i>
            {order.orderNote.length > 40 ? order.orderNote.slice(0, 40) + '…' : order.orderNote}
          </div>
        )}

        <div className="order-items-preview">
          {getItems(order).slice(0, 2).map((item, idx) => (
            <div key={idx} className="item-preview">
              <span>{dishName(item)}</span>
              <span>x{item.quantity}</span>
            </div>
          ))}
          {getItems(order).length > 2 && <div className="more-items">+{getItems(order).length - 2} {t('orders.more')}</div>}
        </div>

        <div className="order-card-footer">
          <div className="order-time"><i className="bi bi-clock"></i> {formatTime(order.createdAt)}</div>
          <div className="order-total">¥{order.totalAmount.toFixed(2)}</div>
        </div>
      </div>

      {type === 'new' && (
        <div className="order-card-actions">
          <button className="btn-accept" onClick={e => { e.stopPropagation(); handleAcceptOrder(order._id); }}>
            <i className="bi bi-check2-circle"></i> {t('orders.accept')}
          </button>
          <button className="btn-cancel" onClick={e => { e.stopPropagation(); handleCancelOrder(order._id); }}>
            <i className="bi bi-x-circle"></i> {t('orders.cancel')}
          </button>
          <button className="btn-print-icon" onClick={e => { e.stopPropagation(); printReceipt(order); }} title={t('orders.printReceipt')}>
            <i className="bi bi-printer-fill"></i>
          </button>
        </div>
      )}

      {type === 'preparing' && (
        <div className="order-card-actions">
          <button className="btn-eta" onClick={e => { e.stopPropagation(); setEtaModal({ open: true, orderId: order._id }); }}>
            <i className="bi bi-clock"></i> {t('orders.setEta')}
          </button>
          <button className="btn-ready" onClick={e => { e.stopPropagation(); handleMarkReady(order._id); }}>
            <i className="bi bi-bell"></i> {t('orders.markReady')}
          </button>
          <button className="btn-cancel" onClick={e => { e.stopPropagation(); handleCancelOrder(order._id); }}>
            <i className="bi bi-x-circle"></i> {t('orders.cancel')}
          </button>
          <button className="btn-print-icon" onClick={e => { e.stopPropagation(); printReceipt(order); }} title={t('orders.printReceipt')}>
            <i className="bi bi-printer-fill"></i>
          </button>
        </div>
      )}

      {type === 'ready' && (
        <div className="order-card-actions">
          <button className="btn-complete" onClick={e => { e.stopPropagation(); handleCompleteOrder(order._id); }}>
            <i className="bi bi-check-all"></i> {t('orders.complete')}
          </button>
          <button className="btn-print-icon" onClick={e => { e.stopPropagation(); printReceipt(order); }} title={t('orders.printReceipt')}>
            <i className="bi bi-printer-fill"></i>
          </button>
        </div>
      )}
    </motion.div>
  );

  const renderHistoryTable = (orderList, showStatus = true) => {
    const allChecked = orderList.length > 0 && orderList.every(o => selectedIds.has(o._id));
    const someChecked = orderList.some(o => selectedIds.has(o._id));
    return (
      <div>
        {someChecked && (
          <div className="selected-actions-bar">
            <span className="selected-count">{selectedIds.size} {t('orders.selected')}</span>
            {orderList.filter(o => selectedIds.has(o._id)).length >= 2 && (
              <button
                className="btn-combine-selected"
                onClick={() => printCombinedReceipt(orderList.filter(o => selectedIds.has(o._id)))}
              >
                <i className="bi bi-receipt-cutoff"></i> {t('orders.combinedBill')}
              </button>
            )}
            <button
              className="btn-delete-selected"
              onClick={() => setDeleteSelectedConfirm(true)}
              disabled={deleting}
            >
              <i className="bi bi-trash3-fill"></i> {t('orders.deleteSelected')}
            </button>
            <button className="btn-clear-selection" onClick={() => setSelectedIds(new Set())}>
              {t('orders.clear')}
            </button>
          </div>
        )}
        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={() => toggleSelectAll(orderList)}
                  />
                </th>
                <th>Order ID</th>
                <th>Table</th>
                <th>Items</th>
                <th>Total</th>
                {showStatus && <th>Status</th>}
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {orderList.map(order => (
                <tr
                  key={order._id}
                  onClick={() => setSelectedOrder(order)}
                  className={selectedIds.has(order._id) ? 'row-selected' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="checkbox-col" onClick={e => toggleSelectOrder(order._id, e)}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(order._id)}
                      onChange={() => {}}
                    />
                  </td>
                  <td className="order-id-cell">#{order.orderNumber || order._id.slice(-6).toUpperCase()}</td>
                  <td>{tableDisplay(order)}</td>
                  <td>
                    <div className="items-cell">
                      {getItems(order).map((item, idx) => (
                        <span key={idx}>{dishName(item)} x{item.quantity}</span>
                      ))}
                    </div>
                  </td>
                  <td className="total-cell">¥{order.totalAmount.toFixed(2)}</td>
                  {showStatus && (
                    <td>
                      <span className={`status-tag ${order.status.toLowerCase()}`}>{order.status}</span>
                    </td>
                  )}
                  <td className="time-cell">{formatDate(order.createdAt)} {formatTime(order.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="orders-loading">
        <div className="loading-spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="orders-wrapper">
      {/* Confirm modal */}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.action === 'cancel' ? t('confirm.cancelOrder') : t('confirm.completeOrder')}
        message={confirmModal.label}
        confirmText={confirmModal.action === 'cancel' ? t('confirm.cancelOrder') : t('orders.complete')}
        confirmVariant={confirmModal.action === 'cancel' ? 'danger' : 'success'}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmModal({ open: false, action: null, orderId: null, label: '' })}
      />

      {/* ETA Modal */}
      <AnimatePresence>
        {etaModal.open && (
          <motion.div className="order-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEtaModal({ open: false, orderId: null })}>
            <motion.div className="order-modal" style={{ maxWidth: 360 }} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2><i className="bi bi-clock me-2"></i>{t('orders.setEtaTitle')}</h2>
                <button className="close-modal" onClick={() => setEtaModal({ open: false, orderId: null })}><i className="bi bi-x-lg"></i></button>
              </div>
              <div className="modal-body">
                <p style={{ color: '#6B5347', marginBottom: '1rem' }}>{t('orders.etaQuestion')}</p>
                <div className="eta-picker">
                  {[10, 15, 20, 30, 45].map(m => (
                    <button key={m} className={`eta-chip ${etaMinutes === m ? 'active' : ''}`} onClick={() => setEtaMinutes(m)}>{m} min</button>
                  ))}
                </div>
                <input
                  type="number" min="1" max="120"
                  className="form-input mt-3"
                  value={etaMinutes}
                  onChange={e => setEtaMinutes(parseInt(e.target.value) || 15)}
                  style={{ textAlign: 'center' }}
                />
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setEtaModal({ open: false, orderId: null })}>{t('common.cancel')}</button>
                <button className="btn-accept" onClick={handleSetETA}><i className="bi bi-check-lg me-1"></i>{t('orders.setEta')}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="orders-header">
        <div className="header-left">
          <h1><i className="bi bi-receipt"></i> {t('orders.title')}</h1>
          <p className="header-subtitle">
            {sseConnected ? (
              <span style={{ color: '#228B22' }}><i className="bi bi-broadcast me-1"></i>{t('orders.liveActive')}</span>
            ) : (
              <span style={{ color: '#c9a227' }}><i className="bi bi-arrow-repeat me-1"></i>{t('orders.polling')}</span>
            )}
          </p>
        </div>
        <button className="refresh-btn" onClick={fetchOrders}>
          <i className="bi bi-arrow-clockwise"></i> {t('common.refresh')}
        </button>
      </div>

      <div className="orders-tabs">
        <button className={`tab-btn ${activeTab === 'new' ? 'active' : ''} ${newOrderAlert ? 'new-order-pulse' : ''}`} onClick={() => setActiveTab('new')}>
          <span className="tab-icon">🆕</span> {t('orders.newOrders')}
          {getTabCount('new') > 0 && <span className="tab-count">{getTabCount('new')}</span>}
        </button>
        <button className={`tab-btn ${activeTab === 'preparing' ? 'active' : ''}`} onClick={() => setActiveTab('preparing')}>
          <span className="tab-icon">👨‍🍳</span> {t('orders.preparing')}
          {getTabCount('preparing') > 0 && <span className="tab-count">{getTabCount('preparing')}</span>}
        </button>
        <button className={`tab-btn ${activeTab === 'ready' ? 'active' : ''}`} onClick={() => setActiveTab('ready')}>
          <span className="tab-icon">🔔</span> {t('orders.ready')}
          {getTabCount('ready') > 0 && <span className="tab-count">{getTabCount('ready')}</span>}
        </button>
        <button className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => { setActiveTab('history'); setSelectedIds(new Set()); }}>
          <span className="tab-icon">📋</span> {t('orders.history')} ({orders.completed.length})
        </button>
      </div>

      <div className="orders-content">
        <AnimatePresence mode="wait">
          {activeTab === 'new' && (
            <motion.div key="new" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="orders-grid">
              {orders.new.length === 0 ? (
                <div className="empty-tab"><i className="bi bi-inbox"></i><h3>{t('orders.noNew')}</h3><p>{t('orders.noNewSub')}</p></div>
              ) : orders.new.map(order => renderOrderCard(order, 'new'))}
            </motion.div>
          )}

          {activeTab === 'preparing' && (
            <motion.div key="preparing" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="orders-grid">
              {orders.preparing.length === 0 ? (
                <div className="empty-tab"><i className="bi bi-clock-history"></i><h3>{t('orders.noPreparing')}</h3><p>{t('orders.noPreparingSub')}</p></div>
              ) : orders.preparing.map(order => renderOrderCard(order, 'preparing'))}
            </motion.div>
          )}

          {activeTab === 'ready' && (
            <motion.div key="ready" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="orders-grid">
              {orders.ready.length === 0 ? (
                <div className="empty-tab"><i className="bi bi-check-circle"></i><h3>{t('orders.noReady')}</h3><p>{t('orders.noReadySub')}</p></div>
              ) : orders.ready.map(order => renderOrderCard(order, 'ready'))}
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div key="history" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="history-section">
              {/* Delete Records Panel */}
              <div className="delete-records-panel">
                <button className="delete-toggle-btn" onClick={() => setShowDeletePanel(p => !p)}>
                  <i className="bi bi-trash3"></i> Delete Records
                  <i className={`bi bi-chevron-${showDeletePanel ? 'up' : 'down'} ms-2`}></i>
                </button>
                {showDeletePanel && (
                  <div className="delete-range-form">
                    <span className="delete-range-label">Delete all orders from</span>
                    <input type="date" className="date-input" value={deleteFrom} onChange={e => setDeleteFrom(e.target.value)} />
                    <span className="delete-range-label">to</span>
                    <input type="date" className="date-input" value={deleteTo} onChange={e => setDeleteTo(e.target.value)} />
                    <button
                      className="btn-delete-range"
                      onClick={() => setDeleteConfirm(true)}
                      disabled={!deleteFrom || !deleteTo || deleting}
                    >
                      <i className="bi bi-trash3-fill"></i> {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>

              <ConfirmModal
                isOpen={deleteConfirm}
                title="Delete Order Records"
                message={`This will permanently delete ALL orders from ${deleteFrom} to ${deleteTo}. This cannot be undone.`}
                confirmText="Delete Forever"
                confirmVariant="danger"
                onConfirm={() => { setDeleteConfirm(false); handleBulkDelete(); }}
                onCancel={() => setDeleteConfirm(false)}
              />

              <ConfirmModal
                isOpen={deleteSelectedConfirm}
                title="Delete Selected Orders"
                message={`Delete ${selectedIds.size} selected order${selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.`}
                confirmText="Delete"
                confirmVariant="danger"
                onConfirm={() => { setDeleteSelectedConfirm(false); handleDeleteSelected(); }}
                onCancel={() => setDeleteSelectedConfirm(false)}
              />

              <div className="history-sub-tabs">
                <button className={`history-sub-tab ${historySubTab === 'completed' ? 'active' : ''}`} onClick={() => { setHistorySubTab('completed'); setSelectedIds(new Set()); }}>
                  <i className="bi bi-check-circle"></i> Completed ({orders.completed.length})
                </button>
                <button className={`history-sub-tab ${historySubTab === 'cancelled' ? 'active' : ''}`} onClick={() => { setHistorySubTab('cancelled'); setSelectedIds(new Set()); }}>
                  <i className="bi bi-x-circle"></i> Cancelled ({orders.cancelled.length})
                </button>
              </div>

              <div className="history-controls">
                <div className="search-box">
                  <i className="bi bi-search"></i>
                  <input type="text" placeholder="Search by Order ID or Table..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
                <div className="time-filters">
                  <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="time-select">
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                  {timeFilter === 'custom' && (
                    <div className="custom-dates">
                      <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                      <span>to</span>
                      <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                    </div>
                  )}
                </div>
              </div>

              <div className="history-stats">
                {historySubTab === 'completed' && (
                  <>
                    <div className="stat-item">
                      <span className="stat-label">Total Orders</span>
                      <span className="stat-value">{getFilteredCompleted().length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Revenue</span>
                      <span className="stat-value revenue">¥{getFilteredCompleted().reduce((s, o) => s + o.totalAmount, 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
                {historySubTab === 'cancelled' && (
                  <div className="stat-item">
                    <span className="stat-label">Cancelled Orders</span>
                    <span className="stat-value cancelled">{getFilteredCancelled().length}</span>
                  </div>
                )}
              </div>

              {historySubTab === 'completed' && (
                getFilteredCompleted().length === 0 ? (
                  <div className="empty-tab"><i className="bi bi-journal-text"></i><h3>No Completed Orders</h3></div>
                ) : renderHistoryTable(getFilteredCompleted(), false)
              )}
              {historySubTab === 'cancelled' && (
                getFilteredCancelled().length === 0 ? (
                  <div className="empty-tab"><i className="bi bi-journal-x"></i><h3>No Cancelled Orders</h3></div>
                ) : renderHistoryTable(getFilteredCancelled(), true)
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating combined-bill bar (active tabs only) */}
      <AnimatePresence>
        {(activeTab === 'new' || activeTab === 'preparing' || activeTab === 'ready') && getSelectedActiveOrders().length >= 2 && (
          <motion.div
            className="combined-bill-bar"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
          >
            <span className="combined-count">
              <i className="bi bi-receipt-cutoff me-2"></i>
              {getSelectedActiveOrders().length} orders selected
            </span>
            <button className="btn-combined-print" onClick={handlePrintCombined}>
              <i className="bi bi-printer-fill me-1"></i> Print Combined Bill
            </button>
            <button className="btn-combined-clear" onClick={() => setSelectedIds(new Set())}>
              Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Order detail modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div className="order-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedOrder(null)}>
            <motion.div className="order-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('orders.orderDetails')}</h2>
                <button className="close-modal" onClick={() => setSelectedOrder(null)}><i className="bi bi-x-lg"></i></button>
              </div>

              <div className="modal-body">
                <div className="order-info-row">
                  <div className="info-item">
                    <span className="info-label">{t('orders.orderId')}</span>
                    <span className="info-value">#{selectedOrder.orderNumber || selectedOrder._id.slice(-6).toUpperCase()}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('orders.table')}</span>
                    <span className="info-value">{tableDisplay(selectedOrder)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('orders.status')}</span>
                    <span className={`status-badge ${selectedOrder.status.toLowerCase()}`}>{selectedOrder.status}</span>
                  </div>
                </div>

                {/* Order Note */}
                {selectedOrder.orderNote && (
                  <div className="order-note-box">
                    <span className="note-label"><i className="bi bi-chat-left-text me-1"></i>{t('orders.orderNote')}</span>
                    <p className="note-text">{selectedOrder.orderNote}</p>
                  </div>
                )}

                {/* ETA */}
                {selectedOrder.estimatedReadyTime && (
                  <div className="order-eta-box">
                    <i className="bi bi-clock me-2"></i>
                    Estimated ready: <strong>{new Date(selectedOrder.estimatedReadyTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</strong>
                  </div>
                )}

                {/* Coupon */}
                {selectedOrder.couponCode && (
                  <div className="order-coupon-box">
                    <i className="bi bi-tag me-2"></i>
                    Coupon: <strong>{selectedOrder.couponCode}</strong>
                    {selectedOrder.discountAmount > 0 && <span> — ¥{selectedOrder.discountAmount.toFixed(2)} off</span>}
                  </div>
                )}

                <div className="order-items-section">
                  <h3>{t('orders.orderItems')}</h3>
                  <div className="items-list">
                    {getItems(selectedOrder).map((item, idx) => (
                      <div key={idx} className="order-item">
                        <div className="item-info">
                          <span className="item-name">
                            {dishName(item)}
                            {item.variantLabel && <span style={{ color: '#a05a2c', fontWeight: 600, marginLeft: 6 }}>· {item.variantLabel}</span>}
                          </span>
                          <span className="item-price">¥{Number(item.price).toFixed(2)} x {item.quantity}</span>
                        </div>
                        <span className="item-subtotal">¥{Number(item.subtotal).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="order-total-section">
                  {selectedOrder.discountAmount > 0 && (
                    <div className="ots-row" style={{ color: '#8ee68e' }}>
                      <span>{t('orders.coupon')}</span>
                      <span>-¥{Number(selectedOrder.discountAmount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="ots-row">
                    <span>{t('orders.total')}</span>
                    <span>¥{Number(selectedOrder.totalAmount).toFixed(2)}</span>
                  </div>
                  <div className="ots-row">
                    <span>{t('orders.salesTax')}</span>
                    <span>¥{Number(selectedOrder.salesTaxAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="ots-row" style={{ color: '#8ee68e' }}>
                    <span>{t('orders.discount')}</span>
                    <span>-¥{Number(selectedOrder.orderDiscountAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="ots-row ots-grand">
                    <span>{t('orders.grandTotal')}</span>
                    <span className="total-amount">¥{(Number(selectedOrder.totalAmount) + Number(selectedOrder.salesTaxAmount || 0) - Number(selectedOrder.orderDiscountAmount || 0)).toFixed(2)}</span>
                  </div>
                </div>

                <div className="order-time-section">
                  <div className="time-item">
                    <span className="time-label">{t('orders.created')}</span>
                    <span className="time-value">{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>


              <div className="modal-footer">
                <button className="btn-print" onClick={() => printReceipt(selectedOrder)} title={t('orders.printReceipt')}>
                  <i className="bi bi-printer"></i> {t('common.print')}
                </button>
                {(selectedOrder.status === 'New' || selectedOrder.status === 'Pending') && (
                  <>
                    <button className="btn-accept" onClick={() => handleAcceptOrder(selectedOrder._id)}><i className="bi bi-check2-circle"></i> {t('orders.acceptOrder')}</button>
                    <button className="btn-cancel" onClick={() => handleCancelOrder(selectedOrder._id)}><i className="bi bi-x-circle"></i> {t('orders.cancel')}</button>
                  </>
                )}
                {selectedOrder.status === 'Preparing' && (
                  <>
                    <button className="btn-eta" onClick={() => setEtaModal({ open: true, orderId: selectedOrder._id })}><i className="bi bi-clock"></i> {t('orders.setEta')}</button>
                    <button className="btn-ready" onClick={() => handleMarkReady(selectedOrder._id)}><i className="bi bi-bell"></i> {t('orders.markReady')}</button>
                    <button className="btn-cancel" onClick={() => handleCancelOrder(selectedOrder._id)}><i className="bi bi-x-circle"></i> {t('orders.cancel')}</button>
                  </>
                )}
                {selectedOrder.status === 'Ready' && (
                  <button className="btn-complete" onClick={() => handleCompleteOrder(selectedOrder._id)}><i className="bi bi-check-all"></i> {t('orders.completeOrder')}</button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Orders;
